/**
 * Сообщения родителю iframe: высота контента (ADR-0008; эталон monolith ADR-0010).
 * Секреты и токены через postMessage не передаём.
 *
 * В URL iframe задайте `embed_parent_origin=https://сайт-партнёра.ru` — origin для `postMessage` (не используйте `*` в проде).
 * В dev без параметра допускается `targetOrigin` `*` для отладки.
 */

export const EMBED_POSTMESSAGE_SOURCE = 'domcrm-embed' as const;

export type EmbedToParentResizeMessage = {
  source: typeof EMBED_POSTMESSAGE_SOURCE;
  type: 'resize';
  /** Высота контента в px (округление вверх). */
  height: number;
};

const DEBOUNCE_MS = 80;

function getTargetOrigin(): string | null {
  const raw = new URLSearchParams(window.location.search).get('embed_parent_origin')?.trim();
  if (raw) {
    try {
      return new URL(raw).origin;
    } catch {
      if (import.meta.env.DEV) {
        return '*';
      }
      return null;
    }
  }
  if (import.meta.env.DEV) {
    return '*';
  }
  return null;
}

function measureDocHeight(): number {
  const vp = document.getElementById('domcrm-embed-viewport');
  if (vp) {
    const h = vp.getBoundingClientRect().height;
    return Math.ceil(Math.max(h, 400));
  }
  const el = document.documentElement;
  const body = document.body;
  const h = Math.max(
    el?.scrollHeight ?? 0,
    body?.scrollHeight ?? 0,
    el?.getBoundingClientRect().height ?? 0
  );
  return Math.ceil(Math.max(h, 400));
}

let resizeTimer: ReturnType<typeof setTimeout> | null = null;

function postResize(height: number, targetOrigin: string): void {
  const msg: EmbedToParentResizeMessage = {
    source: EMBED_POSTMESSAGE_SOURCE,
    type: 'resize',
    height
  };
  try {
    window.parent.postMessage(msg, targetOrigin);
  } catch {
    /* ignore */
  }
}

function scheduleResize(targetOrigin: string): void {
  if (resizeTimer != null) {
    clearTimeout(resizeTimer);
  }
  resizeTimer = setTimeout(() => {
    resizeTimer = null;
    postResize(measureDocHeight(), targetOrigin);
  }, DEBOUNCE_MS);
}

/**
 * Включает отчёт о высоте при встраивании в iframe (не WebView полноэкранный).
 * Идемпотентно: повторный вызов не дублирует подписки.
 */
export function initEmbedParentBridge(): void {
  if (typeof window === 'undefined') return;
  try {
    if (window.parent === window) {
      return;
    }
  } catch {
    return;
  }

  const targetOrigin = getTargetOrigin();
  if (!targetOrigin) {
    return;
  }

  const ro = new ResizeObserver(() => scheduleResize(targetOrigin));
  ro.observe(document.documentElement);
  if (document.body) {
    ro.observe(document.body);
  }

  window.addEventListener('load', () => scheduleResize(targetOrigin));
  scheduleResize(targetOrigin);
}
